import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable, of} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {TableauPage} from '@models/sidebar/response/tableau-page';
import {catchError, map, shareReplay} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TableauPageConfigurationService {
  private GET_TABLEAU_PROJECTS_FOR_ORGANIZATION_URL_SUFFIX = '/api/web/base/:machineOrGroupId/tableauPage/:tenant/projects';
  private GET_TABLEAU_VIEWS_FOR_ORGANIZATION_AND_PROJECT_URL_SUFFIX = '/api/web/base/:machineOrGroupId/tableauPage/tenant/:tenant/projectId/:projectId';
  private GET_TABLEAU_VIEW_BY_ID_URL_SUFFIX = '/api/web/base/:machineOrGroupId/tableauPage/tableauViews/:pageId/:isParentMachineGroup';
  private EDIT_TABLEAU_VIEW_URL_SUFFIX = '/api/web/base/:machineOrGroupId/tableauPage/tableauViews/edit';

  private tableauViewsResponseCache = new Map();
  private tableauProjectsResponseCache = new Map();

  private readonly BASE_URL: string;

  constructor(private http: HttpClient, private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public getTableauProjectsForOrganisation(machineOrGroupId: number, tenant: number): Observable<any> {
    let url: string = this.BASE_URL + this.GET_TABLEAU_PROJECTS_FOR_ORGANIZATION_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', String(machineOrGroupId))
    .replace(':tenant', String(tenant));

    const projectsFromCache = this.tableauProjectsResponseCache.get(url);
    if (projectsFromCache) {
      return of(projectsFromCache);
    }
    const response = this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe(projects => this.tableauProjectsResponseCache.set(url, projects));
    return response;

  }

  public getTableauViewsForOrganizationAndProject(organizationId: string, projectId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GET_TABLEAU_VIEWS_FOR_ORGANIZATION_AND_PROJECT_URL_SUFFIX;
    url = url.replaceAll(':machineOrGroupId', String(organizationId))
    .replace(':tenant', String(organizationId))
    .replace(':projectId', String(projectId));

    const viewsFromCache = this.tableauViewsResponseCache.get(url);
    if (viewsFromCache) {
      return of(viewsFromCache);
    }
    const response = this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    return response.pipe(
      map((views) => {
        this.tableauViewsResponseCache.set(url, views);
        return views;
      })
    );

  }

  public getTableauViewById(machineOrGroupId: string, pageId: string, isMachineryGroupTab: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.GET_TABLEAU_VIEW_BY_ID_URL_SUFFIX;
    url = url.replaceAll(':machineOrGroupId', machineOrGroupId)
    .replace(':pageId', pageId)
    .replace(':isParentMachineGroup', String(isMachineryGroupTab));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public editTableauView(machineOrGroupId: string, page: TableauPage): Observable<any> {
    let url: string = this.BASE_URL + this.EDIT_TABLEAU_VIEW_URL_SUFFIX;
    url = url.replaceAll(':machineOrGroupId', machineOrGroupId);
    return this.http.post(url, page, HTTPS_REQUEST_OPTIONS);
  }

  public getDate(event): number {
    const date = new Date(Number(event.date));
    date.setHours(Number(event.time.hour));
    date.setMinutes(Number(event.time.minute));
    date.setSeconds(0);
    return date.getTime();
  }
}
